// Lightweight notification sound utility.
// - Uses WebAudio so we don't need an asset file.
// - Respects a per-browser on/off setting in localStorage.
// - Fails silently if the browser blocks audio (e.g. before user interaction).

const STORAGE_KEY = "notification_sound_enabled";

export function isNotificationSoundEnabled(): boolean {
  if (typeof window === "undefined") return true;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === null ? true : v === "1";
}

export function setNotificationSoundEnabled(enabled: boolean) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  window.dispatchEvent(new Event("notification-sound-changed"));
}

let ctx: AudioContext | null = null;

export function playNotificationSound() {
  if (typeof window === "undefined") return;
  if (!isNotificationSoundEnabled()) return;
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return;
    if (!ctx) ctx = new AC();
    if (ctx.state === "suspended") void ctx.resume();

    const now = ctx.currentTime;
    const playTone = (freq: number, start: number, dur: number) => {
      const osc = ctx!.createOscillator();
      const gain = ctx!.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.18, now + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx!.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };
    // Short two-tone "ding"
    playTone(880, 0, 0.12);
    playTone(1320, 0.09, 0.18);
  } catch {
    // ignore — autoplay blocked or audio unavailable
  }
}
